/**
 * LanguageSelectModal
 *
 * Pre-transcription prompt that asks the user which languages are spoken in
 * the recording. The selection is passed directly to the pipeline so that
 * post-processing passes (e.g. Dutch→Afrikaans correction) fire reliably,
 * independent of what the STT engine auto-detects.
 *
 * Languages come from a single tiered config (src/config/languages.ts) so the
 * UI can never offer a code the backend would reject. The South African
 * indigenous tier (Lelapa) is gated on live capability reported by /api/health
 * — disabled "coming soon" until LELAPA_API_KEY is configured, then auto-enabled.
 *
 * Why a modal rather than a profile setting?
 *   Language composition is per-recording metadata, not a persistent user
 *   preference. A code-switching Afrikaans+English meeting and an English-only
 *   training clip may be submitted back-to-back by the same user.
 */

import React, { useEffect, useState } from 'react';
import {
  languagesByTier,
  isLanguageEnabled,
  DEFAULT_LANGUAGE,
  type Capabilities,
  type LanguageOption,
} from '../../config/languages';

interface Props {
  /** Called with the confirmed language selection (may be empty = auto-detect). */
  onConfirm: (languages: string[]) => void;
  /** Called when the user dismisses without confirming (cancels the whole start). */
  onCancel: () => void;
}

export function LanguageSelectModal({ onConfirm, onCancel }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set([DEFAULT_LANGUAGE]));
  const [caps, setCaps] = useState<Capabilities>({});

  // Surface which capability-gated languages are actually live (Lelapa). Fail
  // closed: if /api/health is unreachable, SA-indigenous stays "coming soon".
  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/health', { signal: controller.signal })
      .then((r) => r.json())
      .then((d) => setCaps({
        lelapa: d?.services?.lelapa === 'configured',
        gemini: d?.services?.gemini === 'configured',
      }))
      .catch(() => { /* leave caps empty → gated languages stay disabled */ });
    return () => controller.abort();
  }, []);

  const toggle = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleStart = () => onConfirm([...selected]);
  const handleSkip = () => onConfirm([]); // empty = rely on engine auto-detection only

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onCancel();
  };

  const recommended = languagesByTier('recommended');
  const supported = languagesByTier('supported');
  const indigenous = languagesByTier('sa-indigenous');
  const indigenousLive = indigenous.some((l) => isLanguageEnabled(l, caps));

  /** Full-width selectable row (recommended tier). */
  const renderRow = (lang: LanguageOption) => {
    const active = selected.has(lang.code);
    return (
      <button
        key={lang.code}
        onClick={() => toggle(lang.code)}
        className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left ${
          active
            ? 'border-[#00d4b8] bg-[#00d4b8]/8 shadow-[0_0_0_1px_rgba(0,212,184,0.2)]'
            : 'border-[#e2e8f0] hover:border-gray-300 hover:bg-gray-50'
        }`}
        role="checkbox"
        aria-checked={active}
      >
        <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center shrink-0 transition-all ${
          active ? 'bg-[#00d4b8] border-[#00d4b8]' : 'border-gray-300'
        }`}>
          {active && (
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
        <span className="text-xl leading-none">{lang.flag}</span>
        <div className="flex-1 min-w-0">
          <span className={`text-[13px] font-black uppercase tracking-wide ${active ? 'text-[#007a6d]' : 'text-[#1a2332]'}`}>
            {lang.label}
          </span>
          {lang.note && (
            <p className={`text-[10px] font-bold mt-0.5 leading-none ${active ? 'text-[#00a896]' : 'text-slate-400'}`}>
              {lang.note}
            </p>
          )}
        </div>
      </button>
    );
  };

  /** Compact selectable pill (supported + live indigenous tiers). */
  const renderPill = (lang: LanguageOption) => {
    const active = selected.has(lang.code);
    return (
      <button
        key={lang.code}
        onClick={() => toggle(lang.code)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 text-[10px] font-bold uppercase tracking-wide transition-all ${
          active
            ? 'border-[#00d4b8] bg-[#00d4b8]/8 text-[#007a6d]'
            : 'border-[#e2e8f0] text-[#1a2332] hover:border-gray-300 hover:bg-gray-50'
        }`}
        role="checkbox"
        aria-checked={active}
      >
        <span>{lang.flag}</span>
        {lang.label}
      </button>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lang-modal-heading"
      onClick={handleBackdrop}
    >
      <div className="bg-white rounded-[32px] shadow-2xl w-full max-w-sm p-8 space-y-7 animate-in max-h-[90vh] overflow-y-auto">

        {/* Heading */}
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-[#00d4b8] mb-2">
            Before You Start
          </p>
          <h2
            id="lang-modal-heading"
            className="text-[18px] font-black uppercase tracking-tight text-[#1a2332] leading-tight"
          >
            Languages in this recording
          </h2>
          <p className="text-[11px] text-slate-400 font-medium mt-1 leading-snug">
            Select all languages spoken. Multiple selections are fine.
          </p>
        </div>

        {/* Recommended — tuned for South African accents */}
        <div className="space-y-3">
          <p className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400">
            Recommended for South Africa
          </p>
          {recommended.map(renderRow)}
          <p className="text-[10px] text-slate-400 font-medium leading-snug">
            English &amp; Afrikaans are tuned for South African accents, including code-switching.
          </p>
        </div>

        {/* Supported — global languages (small files: prompted Gemini; large files: AssemblyAI) */}
        <div className="border-t border-[#e2e8f0] pt-5 space-y-3">
          <p className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400">
            More supported languages
          </p>
          <div className="flex flex-wrap gap-2">
            {supported.map(renderPill)}
          </div>
        </div>

        {/* South African languages — Lelapa-gated */}
        <div className="border-t border-[#e2e8f0] pt-5 space-y-3">
          <p className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400">
            {indigenousLive ? 'South African languages — beta' : 'South African languages — coming soon'}
          </p>
          {indigenousLive && (
            <p className="text-[10px] text-slate-400 font-medium leading-snug">
              Best-effort transcription via ElevenLabs Scribe (with Gemini fallback). Accuracy varies — short, clear recordings work best.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {indigenous.map((lang) =>
              isLanguageEnabled(lang, caps) ? (
                renderPill(lang)
              ) : (
                <span
                  key={lang.code}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-50 border border-[#e2e8f0] text-[10px] font-bold text-slate-300 cursor-not-allowed"
                  aria-disabled="true"
                >
                  <span>{lang.flag}</span>
                  {lang.label}
                </span>
              )
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3 pt-1">
          <button
            onClick={handleStart}
            className="btn-primary w-full py-5 rounded-[24px] text-[11px] tracking-[0.4em] font-black uppercase"
          >
            Start Transcription
          </button>
          <button
            onClick={handleSkip}
            className="w-full py-2 text-[10px] font-bold text-slate-400 hover:text-slate-500 uppercase tracking-widest transition-colors"
          >
            Skip — auto-detect only
          </button>
        </div>
      </div>
    </div>
  );
}
